/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2016 Michał Osadnik osdnk
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { useMemo } from 'react';
import { View, Platform } from 'react-native';
import { useAnimatedProps, useAnimatedStyle, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import ChartPath from './ChartPath';
import useLongPressGestureHandler from './hooks/useLongPressGestureHandler';
import useGenerateValues from './hooks/useGenerateValues';
import { calculateProgressingPath } from './worklets/path';
import calculateCurrentPriceCircleProps from './worklets/currentPriceCircle';
const ChartPathProvider = (props) => {
    const { contextValue, hapticsEnabled = true, children, color, selectedStrokeWidth, selectedOpacity, strokeWidth, ...rest } = props;
    const generatedValues = useGenerateValues();
    const pathProps = useMemo(() => ({ strokeWidth }), [strokeWidth]);
    const hitSlop = 30;
    const { dotScale, originalX, originalY, pathOpacity, positionX, positionY, progress, layoutSize, state, // gesture state
    data, dataFiltered, prevPeriod, currPeriod, originalDataSet, currentPriceCircleRadius, } = contextValue || generatedValues;
    const hitSlopValue = useSharedValue(hitSlop);
    const hapticsEnabledValue = useSharedValue(hapticsEnabled);
    const selectedStrokeWidthValue = useSharedValue(selectedStrokeWidth);
    const selectedOpacityShared = useSharedValue(selectedOpacity);
    const strokeWidthValue = useSharedValue(strokeWidth);
    const isStarted = useSharedValue(false);
    const longPressGesture = useLongPressGestureHandler({
        state,
        data,
        isStarted,
        dotScale,
        pathOpacity,
        hapticsEnabledValue,
        hitSlopValue,
        layoutSize,
        dataFiltered,
        positionX,
        positionY,
        originalX,
        originalY,
        originalDataSet,
        progress,
        currPeriod,
    });
    const path = useDerivedValue(() => calculateProgressingPath(dataFiltered.value[currPeriod.value], prevPeriod.value ? dataFiltered.value[prevPeriod.value] : [], progress.value, layoutSize.value));
    const animatedPathProps = useAnimatedProps(() => {
        const props = {
            d: typeof path.value === 'string' ? path.value : '',
            stroke: color.value,
        };
        return props;
    }, [color, path]);
    const animatedShadowProps = useAnimatedProps(() => ({
        shadowColor: color.value,
    }), [color]);
    const animatedStrokeProps = useAnimatedProps(() => {
        const props = {
            strokeWidth: pathOpacity.value * (Number(strokeWidthValue.value) - Number(selectedStrokeWidthValue.value)) +
                Number(selectedStrokeWidthValue.value),
        };
        return props;
    });
    const animatedPathStyle = useAnimatedStyle(() => {
        const style = {};
        if (Platform.OS === 'ios') {
            style.opacity = pathOpacity.value * (1 - selectedOpacityShared.value) + selectedOpacityShared.value;
        }
        return style;
    }, [pathOpacity, selectedOpacityShared]);
    const animatedCurrentPriceCircleProps = useAnimatedProps(() => calculateCurrentPriceCircleProps(progress.value, data.value[currPeriod.value], prevPeriod.value ? data.value[prevPeriod.value] : [], dataFiltered.value[currPeriod.value], prevPeriod.value ? dataFiltered.value[prevPeriod.value] : [], layoutSize.value, color.value, path.value, currentPriceCircleRadius.value), [color, currPeriod, currentPriceCircleRadius, data, dataFiltered, layoutSize, path, prevPeriod, progress]);
    return (<View>
      <ChartPath animatedPathProps={animatedPathProps} animatedPathStyle={animatedPathStyle} animatedCurrentPriceCircleProps={animatedCurrentPriceCircleProps} longPressGesture={longPressGesture} gestureEnabled layoutSize={layoutSize} pathProps={pathProps} animatedStrokeProps={animatedStrokeProps} animatedShadowProps={animatedShadowProps} {...rest}>
        {children}
      </ChartPath>
    </View>);
};
export default ChartPathProvider;
